import Daily, {
  DailyAdvancedConfig,
  DailyCall,
  DailyCallOptions,
  DailyCallQualityTestResults,
  DailyEvent,
  DailyEventObjectAccessState,
  DailyEventObjectLangUpdated,
  DailyRoomInfo,
} from '@daily-co/daily-js';
// import getConfig from 'next/config';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { getCustomBackground } from '/lib/custom-background';
import { getQueryParam } from '/lib/query';
import {
  leaveRoomPresenceRedis,
} from '/lib/fetchRoomPresence';
import { usePresenceLeaveOnPageClose } from '/hooks/usePresenceLeaveOnPageClose';
import { resolvePresenceUserId } from '/hooks/useRoomPresenceRedis';
import { getRoomQualityReporter } from '/lib/roomQualityReporter';

export type CallMode = 'direct-link' | 'embedded';
export type CallState =
  | 'awaiting-args'
  | 'ready'
  | 'lobby'
  | 'knocking-cancelled'
  | 'knocking-denied'
  | 'joining'
  | 'joined'
  | 'redirecting'
  | 'ended'
  | 'error'
  | 'expired'
  | 'full'
  | 'nbf'
  | 'not-allowed'
  | 'not-found'
  | 'not-secure'
  | 'removed-from-call'
  | 'left'
  | 'missing-payment-method'
  | 'not-supported';

interface Props {
  customHost?: string;
  domain: string;
  room: string;
  token?: string;
  isEmbedded: boolean;
  bypassRegionDetection: boolean;
  v2CamAndMic: boolean | number | null;
  useLegacyVideoProcessor: boolean;
  roomsCheckOrigin?: string;
  apiHost?: string;
  micAudioMode?: DailyAdvancedConfig['micAudioMode'];
  can_close_camera?: boolean;
}

/**
 * This hook sets up the local call state machine and keeps track of the application state.
 * @param domain – The domain name.
 * @param room – The room name.
 * @param token - A meeting token for private meeting access.
 * @param customHost – Optional custom host to connect to.
 * @param isEmbedded - Whether we're running in a daily-js embedded iframe.
 */
export const useCallMachine = ({
  apiHost,
  bypassRegionDetection,
  customHost,
  domain,
  isEmbedded,
  micAudioMode,
  room,
  roomsCheckOrigin,
  token,
  useLegacyVideoProcessor,
  v2CamAndMic,
  can_close_camera = false,
}: Props) => {
  const { i18n } = useTranslation();
  const { query } = useRouter();
  const presenceUserId = useMemo(() => resolvePresenceUserId(query), [query]);
  const qualityReporter = useMemo(() => getRoomQualityReporter(), []);
  const [daily, setDaily] = useState<DailyCall>(null);
  const [state, setState] = useState<CallState>(
    isEmbedded ? 'awaiting-args' : 'ready'
  );
  const [closeOnLeave, setCloseOnLeave] = useState<boolean>(false);
  const [redirectOnLeave, setRedirectOnLeave] = useState<boolean>(false);
  const wasDenied = useRef<boolean>(false);
  const wasKnocking = useRef<boolean>(false);
  const [roomInfo, setRoomInfo] = useState<DailyRoomInfo>(null);
  const isTokenOwner = useMemo(
    () => Boolean(roomInfo?.tokenConfig?.is_owner),
    [roomInfo]
  );
  const [callArgs, setCallArgs] = useState<DailyCallOptions>({});

  const url = useMemo(() => {
    if (domain && room) {
      let roomUrl = `https://${customHost ? customHost : `${domain}.daily.co`
        }/${room}`;
      const params = new URLSearchParams();
      if (customHost) {
        params.append('domain', domain);
      }
      if (bypassRegionDetection) {
        params.append('bypassRegionDetection', 'true');
      }
      if (roomsCheckOrigin) {
        params.append('roomsCheckOrigin', roomsCheckOrigin);
      }
      if (apiHost) {
        params.append('apiHost', apiHost);
      }
      roomUrl = `${roomUrl}?${params.toString()}`;
      return roomUrl;
    }
    return null;
  }, [
    domain,
    room,
    customHost,
    bypassRegionDetection,
    roomsCheckOrigin,
    apiHost,
  ]);

  const disableAudio = useMemo(
    () => new URLSearchParams(window.location.search).get('audio') === 'false',
    []
  );

  /**
   * Galaxy suppresses the prejoin/haircheck screen per joining link by putting
   * `prejoin=false` on the URL, so one class can drop its participants straight
   * into the call without moving the room or domain default for everyone else.
   *
   * `enable_prejoin_ui` cannot do this from Galaxy's side: it is a room/domain
   * property, not a meeting token one, so a self-signed token carrying it is
   * discarded and `tokenConfig` below never sees it.
   *
   * Absent - or any value other than `false` - leaves Daily's own config in
   * charge, so links that predate this deploy behave exactly as before.
   */
  const prejoinDisabledByParam = useMemo(() => {
    if (getQueryParam('prejoin', query) === 'false') return true;
    if (typeof window === 'undefined') return false;
    return (
      new URLSearchParams(window.location.search).get('prejoin') === 'false'
    );
  }, [query]);

  /**
   * ...but only for the session daily-js is actually driving, which is the one
   * carrying a meeting token.
   *
   * `prejoin=false` travels on the joining link, and that link gets loaded more
   * than once: galaxy hands the same URL to `DailyIframe.wrap()` AND assigns it
   * to the iframe's `src` elsewhere, and only the wrap/join pass appends the
   * `t` token. Skipping the pre-join on every load turned those token-less
   * loads into real, anonymous participants -- one page load put three copies of
   * the same teacher in the room, two of them with no user id at all. The
   * pre-join screen used to absorb them: a stray load parked there and never
   * joined anything.
   *
   * Requiring the token restores that: a load without one shows the pre-join and
   * stays harmless, while the real embedded session skips it as intended. The
   * token is read from `t` once and kept in state (the URL is scrubbed right
   * after, see pages/[...room].tsx), so this does not flip back mid-session.
   *
   * The skip is for students only: an owner (teacher) still gets the pre-join,
   * since that is where the owner's "waiting for students" lobby lives. Owner
   * status comes from the token, so it is only known after preAuth and is
   * checked in prejoinUIEnabled below.
   */
  const shouldSkipPrejoin = prejoinDisabledByParam && Boolean(token);

  /**
   * Helper method to determine whether we want to show the prejoin UX.
   * @param co
   */
  const prejoinUIEnabled = async (co: DailyCall) => {
    const room = (await co.room()) as DailyRoomInfo;
    if (shouldSkipPrejoin && !room?.tokenConfig?.is_owner) return false;

    const { access } = co.accessState();

    // Prejoin config priorities: Token > Room > Domain
    const prejoinEnabled = Boolean(
      room?.tokenConfig?.enable_prejoin_ui ??
      room?.config?.enable_prejoin_ui ??
      room?.domainConfig?.enable_prejoin_ui
    );
    const knockingEnabled = !!room?.config?.enable_knocking;

    return (
      prejoinEnabled ||
      (access !== 'unknown' && access?.level === 'lobby' && knockingEnabled)
    );
  };

  /**
   * Joins the call and tries to init with previously stored devices.
   * @param co – The DailyCall object.
   */
  const join = useCallback(
    async (co: DailyCall) => {
      setState('joining');

      /**
       * END THE PRE-CALL TEST BEFORE THE CALL STARTS.
       *
       * The lobby branch below fires co.testCallQuality() and never stops it. That test
       * is not a passive measurement: it creates a pre-call room on Daily's side (the
       * /create-precall-room request) and runs a media pipeline to measure the link. It
       * is also stateful -- daily-js refuses other calls while one is in progress, with
       * "A pre-call quality test is in progress ... Use stopTestCallQuality() to end it
       * early" -- so a test still running after the student has joined is competing with
       * the real call for the same encode and decode budget on a device that, in this
       * product, is usually integrated graphics already decoding a classroom of tiles.
       *
       * Nothing in this app has ever called stopTestCallQuality. Joining quickly -- which
       * is what a student does when the teacher is already waiting -- left it running.
       */
      try {
        await co.stopTestCallQuality();
      } catch (e) {
        // No test in flight, or the call object is already gone. Neither is a reason not
        // to join, which is the whole point of swallowing it here.
      }
      const room = (await co.room()) as DailyRoomInfo;

      // Force mute clients when joining a call with experimental_optimize_large_calls enabled.
      if (room?.config?.experimental_optimize_large_calls) {
        co.setLocalAudio(false);
      }

      // Telemetry only, and never awaited: the join path must not wait on it.
      qualityReporter.noteJoinAttempt();

      try {
        await co.join({
          subscribeToTracksAutomatically: false,
          token,
          url,
        });
      } catch (e) {
        qualityReporter.noteJoinFailure(e);
        throw e;
      }
      setState('joined');
    },
    [qualityReporter, token, url]
  );

  /**
   * Preauthenticates, so we know about the user's access state and the room's config.
   * Puts the machine into the next state, based on access state and room config.
   * @param co – The DailyCall object.
   */
  const preAuth = useCallback(
    async (co: DailyCall) => {
      const { access } = await co.preAuth({
        subscribeToTracksAutomatically: false,
        token,
        url,
      });

      co.startLocalAudioLevelObserver(100);
      co.startRemoteParticipantsAudioLevelObserver(100);

      const room = (await co.room()) as DailyRoomInfo;
      const { lang } = await co.getDailyLang();
      if (
        room?.domainConfig &&
        room.domainConfig['attach_callobject_to_window']
      ) {
        window['callObject'] = co;
      }
      i18n.changeLanguage(lang);

      /**
       * Private room and no `token` was passed.
       */
      if (access === 'unknown' || access?.level === 'none') {
        return;
      }

      /**
       * Either `enable_knocking_ui` or `enable_prejoin_ui` is set to `true`.
       */
      if (access?.level === 'lobby' || (await prejoinUIEnabled(co))) {
        setState('lobby');
        /**
         * .catch IS NOT OPTIONAL HERE. The pre-call room request is rate limited, and a
         * 429 rejects this promise -- observed in a staging console, twice in one page
         * load. With only a .then that became an unhandled rejection: noise in every
         * report, and on a page that ships its own error reporting, a false signal.
         *
         * A failed quality test is not a reason to block the lobby. The student simply
         * does not get the bandwidth-optimized downgrade, which is the same position
         * they were in before the test existed.
         */
        co.testCallQuality()
          .then((results: DailyCallQualityTestResults) => {
          switch (results.result) {
            case 'good':
            case 'warning':
              return;
            case 'bad':
              co.updateSendSettings({
                video: 'bandwidth-optimized',
              });
              return;
          }
          })
          .catch(() => {
            // Rate limited, offline, or the object left the lobby before it finished.
          });
        return;
      }

      /**
       * Public room or private room with passed `token` and `enable_prejoin_ui` is `false`.
       */
      join(co);
    },
    [i18n, join, token, url]
  );

  /**
   * Registers the telemetry listeners on a freshly created call object, before
   * anything can call `join()` on it. Synchronous and non-throwing by
   * contract; wrapped anyway, because nothing in telemetry may stop a call
   * from being created.
   */
  const attachQualityReporter = useCallback(
    (co: DailyCall) => {
      try {
        qualityReporter.configure({ roomName: room, userId: presenceUserId });
        qualityReporter.attach(co);
      } catch (e) {
        console.warn('Room quality telemetry failed to attach:', e);
      }
    },
    [presenceUserId, qualityReporter, room]
  );

  /**
   * Sets arguments to pass to createCallObject(), and update state to indicate
   * we're ready to start.
   */
  const initializeCallArgs = useCallback(
    (args: DailyCallOptions) => {
      if (state !== 'awaiting-args') return;
      setCallArgs(args);
      setState('ready');
    },
    [state]
  );

  const leave = useCallback(async () => {
    if (!daily) return;

    const accessState = await daily.accessState();
    wasKnocking.current = 'awaitingAccess' in accessState;
    // If we're in the error state, we've already "left", so just clean up
    if (state === 'error') {
      daily.destroy();
    } else {
      daily.leave();
      if (closeOnLeave) {
        daily.destroy();
        // Set meeting to ended state, in case window can't be closed
        setState('ended');
        window.close();
      }
    }
  }, [closeOnLeave, daily, state]);

  /**
   * Set up the call object and preauthenticate.
   */
  useEffect(() => {
    if (daily || !url || state !== 'ready') return;

    /**
     * Technically navigator.mediaDevices should only be available on secured web contexts,
     * but browsers have a few exceptions to the rule, like localhost, 127.0.0.1 or 0.0.0.0.
     * Instead of trying to guess all the different possibilities for when either Prebuilt
     * or the surrounding web context are running securely or not, we can simply check for
     * navigator.mediaDevices to be defined. navigator.mediaDevices is one of the key APIs
     * we use, anyway.
     */
    if (!navigator.mediaDevices) {
      setState('not-secure');
      return;
    }

    if (!Daily.supportedBrowser().supported) {
      setState('not-supported');
      return;
    }

    // Temporary: determine whether call gets v2 cam and mic treatment
    const v2CamAndMicConfig = callArgs.dailyConfig?.v2CamAndMic ?? v2CamAndMic;

    const args: DailyCallOptions = {
      ...callArgs,
      url,
      dailyConfig: {
        ...callArgs.dailyConfig,
        useDevicePreferenceCookies: true,
        // @ts-ignore
        callMode: isEmbedded ? 'prebuilt-embed' : 'prebuilt-direct',
        useLegacyVideoProcessor:
          // @ts-ignore
          callArgs.dailyConfig?.useLegacyVideoProcessor ??
          useLegacyVideoProcessor,
        //@ts-ignore
        v2CamAndMic: v2CamAndMicConfig,
        micAudioMode,
        can_close_camera,
        ...(micAudioMode !== 'speech'
          ? {
            userMediaAudioConstraints: {
              autoGainControl: false,
              channelCount:
                micAudioMode === 'music' || micAudioMode?.stereo ? 2 : 1,
              echoCancellation: false,
              noiseSuppression: false,
              sampleRate: 48000,
              sampleSize: 16,
              ...(callArgs.dailyConfig?.userMediaAudioConstraints ?? {}),
            },
          }
          : {}),
      },
    };

    const customBackground = getCustomBackground();
    if (customBackground) {
      args.inputSettings = {
        ...(args.inputSettings ?? {}),
        video: {
          processor: {
            type: 'background-image',
            config: {
              source: customBackground,
            },
          },
        },
      };
    }

    /**
     * Resolve the call machine bundle URL.
     *
     * NEXT_PUBLIC_* values are inlined at build time, so a hardcoded absolute
     * URL pins every deployment to one origin and makes the bundle a
     * cross-origin script request (CORS). Instead we resolve whatever we have
     * against the *current* origin at runtime: a relative value stays
     * same-origin on every host the app is deployed to, and an absolute value
     * (set explicitly via env) is still honored as-is.
     */
    const bundleUrl =
      process.env.NEXT_PUBLIC_CALL_MACHINE_URL ||
      '/call-machine-object-bundle-0-92-2.js';
    if (typeof window !== 'undefined') {
      args.dailyConfig.callObjectBundleUrlOverride = new URL(
        bundleUrl,
        window.location.href
      ).href;
    }

    if (disableAudio) {
      // @ts-ignore
      args.audioSource = false;
    }
    const oldCo = Daily.getCallInstance();
    let co;
    if (oldCo) {
      oldCo.destroy().then(() => {
        try {
          co = Daily.createCallObject(args);
          setDaily(co);
          attachQualityReporter(co);
          preAuth(co);
        } catch (e) {
          console.error('Error creating call instance:', e);
          setState('not-supported');
        }
      });
    } else {
      try {
        co = Daily.createCallObject(args);
        setDaily(co);
        attachQualityReporter(co);
        preAuth(co);
      } catch (e) {
        console.error('Error creating call instance:', e);
        setState('not-supported');
      }
    }
  }, [
    attachQualityReporter,
    callArgs,
    daily,
    disableAudio,
    isEmbedded,
    micAudioMode,
    preAuth,
    url,
    state,
    v2CamAndMic,
    useLegacyVideoProcessor,
  ]);

  /**
   * Low-network / high-CPU / join-failure telemetry. Entirely passive: it only
   * reads events the call object already emits, and every listener and flush
   * inside it is wrapped, so nothing here can reach the call.
   *
   * This effect exists for the detach. The listeners themselves are registered
   * by `attachQualityReporter` at the moment the call object is created,
   * because an effect runs a render later -- after `preAuth` has been called,
   * and `preAuth` is what calls `join`. A `join_failed` is the most valuable
   * event in the set, and a listener attached once the join has started never
   * sees it. `attach` is idempotent per call object, so the call here only
   * takes ownership of the cleanup.
   */
  useEffect(() => {
    if (!daily || daily.isDestroyed()) return;

    qualityReporter.configure({ roomName: room, userId: presenceUserId });
    const detach = qualityReporter.attach(daily);
    return detach;
  }, [daily, presenceUserId, qualityReporter, room]);

  /**
   * Listen for access state updates.
   */
  const handleAccessStateUpdated = useCallback(
    async ({ access }: DailyEventObjectAccessState) => {
      /**
       * Ignore initial access-state-updated event.
       */
      const ignoreStates: CallState[] = ['ended', 'awaiting-args', 'ready'];
      if (ignoreStates.includes(state)) return;

      if (access === 'unknown' || access?.level === 'none') {
        setState('not-allowed');
        return;
      }

      const meetingState = daily.meetingState();

      if (access?.level === 'lobby' && meetingState === 'joined-meeting') {
        // Already joined, not need to call join(daily) again.
        return;
      }

      /**
       * 'full' access, we can now join the meeting.
       */
      join(daily);
    },
    [daily, join, state]
  );
  useEffect(() => {
    if (!daily || daily.isDestroyed()) return;

    daily.on('access-state-updated', handleAccessStateUpdated);
    return () => {
      daily.off('access-state-updated', handleAccessStateUpdated);
    };
  }, [daily, handleAccessStateUpdated]);

  /**
   * Set up listeners for meeting state changes
   */
  useEffect(() => {
    if (!daily || daily.isDestroyed()) return;

    const events: DailyEvent[] = [
      'joined-meeting',
      'joining-meeting',
      'left-meeting',
      'error',
    ];

    const handleMeetingState = async (ev) => {
      const { access } = daily.accessState();
      switch (ev.action) {
        /**
         * Don't transition to 'joining' or 'joined' UI as long as access is not 'full'.
         * This means a request to join a private room is not granted, yet.
         * Technically in requesting for access, the participant is already known
         * to the room, but not joined, yet.
         */
        case 'joining-meeting':
          if (
            access === 'unknown' ||
            access.level === 'none' ||
            access.level === 'lobby'
          )
            return;
          setState('joining');
          break;
        case 'joined-meeting':
          if (
            access === 'unknown' ||
            access.level === 'none' ||
            access.level === 'lobby'
          )
            return;
          setRoomInfo((await daily.room()) as DailyRoomInfo);
          setState('joined');
          break;
        case 'left-meeting':
          leaveRoomPresenceRedis(undefined, room, presenceUserId, {
            isOwner: isTokenOwner,
          });
          daily.destroy();
          if (wasKnocking.current) {
            setState('knocking-cancelled');
            break;
          }
          if (wasDenied.current) {
            setState('knocking-denied');
            break;
          }
          if (!redirectOnLeave) {
            setState('left');
            break;
          }
          setState('redirecting');
          break;
        case 'error':
          console.error(ev);
          switch (ev?.error?.type) {
            case 'nbf-room':
            case 'nbf-token':
              daily.destroy();
              setState('nbf');
              break;
            case 'exp-room':
            case 'exp-token':
              daily.destroy();
              setState('expired');
              break;
            case 'ejected':
              daily.destroy();
              setState('removed-from-call');
              break;
            default:
              switch (ev?.errorMsg) {
                case 'Join request rejected':
                  /**
                   * Join request to a private room was denied. We can end here.
                   */
                  wasKnocking.current = false;
                  wasDenied.current = true;
                  daily.leave();
                  break;
                case 'Meeting has ended':
                  /**
                   * Meeting has ended or participant was removed by an owner.
                   */
                  daily.destroy();
                  setState('ended');
                  break;
                case 'Meeting is full':
                  daily.destroy();
                  setState('full');
                  break;
                case "The meeting you're trying to join does not exist.":
                  daily.destroy();
                  setState('not-found');
                  break;
                case 'You are not allowed to join this meeting':
                  daily.destroy();
                  setState('not-allowed');
                  break;
                default:
                  daily.destroy();
                  setState('error');
                  break;
              }
              break;
          }
          break;
      }
    };

    // Listen for changes in state
    for (const event of events) {
      daily.on(event, handleMeetingState);
    }

    // Stop listening for changes in state
    return () => {
      for (const event of events) {
        daily.off(event, handleMeetingState);
      }
    };
  }, [daily, state, domain, room, redirectOnLeave, presenceUserId, isTokenOwner]);

  /**
   * A page that goes away mid-call sends no 'left-meeting', so the leave
   * above never runs for it.
   */
  usePresenceLeaveOnPageClose({
    enabled: state === 'joined',
    roomName: room,
    userId: presenceUserId,
    isOwner: isTokenOwner,
  });

  /**
   * Listen for language changes.
   */
  useEffect(() => {
    if (!daily || daily.isDestroyed()) return;

    const handleLangUpdated = (event: DailyEventObjectLangUpdated) => {
      event.lang && i18n.changeLanguage(event.lang);
    };

    daily.on('lang-updated', handleLangUpdated);

    return () => {
      daily.off('lang-updated', handleLangUpdated);
    };
  }, [daily, i18n]);

  useEffect(() => {
    const getURLWithRecentCallParam = (url: string) => {
      const [base, hash] = url.split('#', 2);
      return `${base + (base.includes('?') ? '&' : '?')}recent-call=${domain}/${roomInfo?.name
        }${hash ? `#${hash}` : ''}`;
    };

    const getRedirectURL = () => {
      if (roomInfo?.tokenConfig?.redirect_on_meeting_exit) {
        return getURLWithRecentCallParam(
          roomInfo?.tokenConfig?.redirect_on_meeting_exit
        );
      }
      if (roomInfo?.domainConfig?.redirect_on_meeting_exit) {
        return getURLWithRecentCallParam(
          roomInfo?.domainConfig?.redirect_on_meeting_exit
        );
      }
    };

    switch (state) {
      case 'joined':
        if (roomInfo?.tokenConfig?.close_tab_on_exit) {
          setRedirectOnLeave(false);
          setCloseOnLeave(true);
        } else if (
          roomInfo?.tokenConfig?.redirect_on_meeting_exit ||
          roomInfo?.domainConfig?.redirect_on_meeting_exit
        ) {
          setRedirectOnLeave(true);
        }
        break;
      case 'redirecting': {
        if (isEmbedded) {
          // Redirect to...nowhere!
          return;
        }
        window.location.href = getRedirectURL();
        break;
      }
    }
  }, [daily, domain, isEmbedded, roomInfo, state]);

  const mode: CallMode = isEmbedded ? 'embedded' : 'direct-link';

  return {
    daily,
    disableAudio,
    leave,
    mode,
    setRedirectOnLeave,
    state,
    initializeCallArgs,
  };
};
